/**
 * /policies/shifts/geofences — Session 3c PR 13.
 * Owning agent: Tashghil.
 */

import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { session3cApi } from "../lib/session3c-api";
import type { GeofenceItem } from "../lib/session3c-api";
import { useLegalEntityId } from "../lib/legalEntity";
import { PolicyListPage } from "./_PolicyListPage";
import type { PolicyColumn } from "./_PolicyListPage";

export function ShiftGeofencesPolicyPage() {
  const { t } = useTranslation();
  const le = useLegalEntityId();

  const query = useQuery({
    queryKey: ["s3c-shift-geofences", le],
    queryFn: () => session3cApi.listGeofences(le ?? ""),
    enabled: !!le,
  });

  const items = query.data?.items ?? [];
  const counts = useMemo(() => ({
    total: items.length,
    attention: 0,
    critical: 0,
  }), [items]);

  const columns: PolicyColumn<GeofenceItem>[] = [
    {
      headerKey: "policies.geofences.col.name",
      label: "Zone name",
      render: (r) => <span className="font-medium">{r.zone_name}</span>,
    },
    {
      headerKey: "policies.geofences.col.type",
      label: "Type",
      render: (r) => <span className="text-xs uppercase text-text-secondary">{r.zone_type}</span>,
    },
    {
      headerKey: "policies.geofences.col.coords",
      label: "Coordinates",
      render: (r) => <span dir="ltr" className="font-mono text-[10px] text-text-tertiary">{r.latitude.toFixed(4)}, {r.longitude.toFixed(4)}</span>,
    },
    {
      headerKey: "policies.geofences.col.radius",
      label: "Radius (m)",
      render: (r) => <span className="font-mono text-xs">{r.radius_m}</span>,
    },
  ];

  return (
    <PolicyListPage<GeofenceItem>
      title={t("policies.geofences.title", "Geofence Definitions")}
      subtitle={t("policies.geofences.subtitle", "Office · branch · WFH-approved zones for attendance verification.")}
      agent="tashghil"
      domain="geofences"
      newButtonLabel={t("policies.geofences.cta.new", "New Geofence")}
      newButtonMode="coming_soon"
      newButtonExpectedRelease="M3X.WORKFLOW"
      query={query}
      columns={columns}
      rowKey={(r) => r.id}
      suggestions={[
        {
          headline: t("policies.geofences.suggestion.clean_headline", `${items.length} rule${items.length === 1 ? "" : "s"} loaded.`),
          body: t("policies.geofences.suggestion.clean_body", "Geofences validate WFH attendance + branch-mode clock-ins. The interactive map picker ships with the M3X.SHIFTS pack."),
          primaryActionLabel: t("policies.geofences.suggestion.audit", "Open audit log"),
          primaryActionOnClick: () => window.location.assign("/settings/audit-log"),
          suggestionKey: "shift-geofences-brief",
        },
      ]}
      briefContext={{
        total: counts.total,
        attention: counts.attention,
        critical: counts.critical,
        top_kind: t("policies.geofences.title", "Geofence Definitions"),
        window_label: t("policies.geofences.window", "active zones"),
      }}
    />
  );
}
